import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import PackageTabs from "../Packages/PackageTabs";

const PackagesList = () => {
  const { user, loading, refreshUserProfile } = useAuth();
  const navigate = useNavigate();

  // AI Package Modal States
  const [showPackageTypeSelectionModal, setShowPackageTypeSelectionModal] = useState(false);
  const [showNoConnectsModal, setShowNoConnectsModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isCheckingConnects, setIsCheckingConnects] = useState(false);

  // State Management
  const [lists, setLists] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  
  // Pagination State
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20); 
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);

  // Deletion States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // State for the Shared Wizard Modal
  const [showWizardModal, setShowWizardModal] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);

  // Fallback placeholder image if no image source is provided
  const placeholderImage = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80";

  // Destination tags for quick filter
  const destinationTags = ["Dubai", "Thailand", "Bangkok", "Singapore", "Maldives", "Bali", "Paris", "London"];

  // AI Package Generation Handler
  const handleGenerateWithAIClick = async () => {
    setIsCheckingConnects(true);
    try {
      const updatedProfile = await refreshUserProfile();
      if (updatedProfile && updatedProfile.connects > 0) {
        setShowPackageTypeSelectionModal(true);
      } else {
        setShowNoConnectsModal(true);
      }
    } catch (error) {
      console.error("Error checking connects:", error);
    } finally {
      setIsCheckingConnects(false);
    }
  };

  // Navigation handlers for AI package types
  const handleNavigateToFlightPackage = () => {
    setShowPackageTypeSelectionModal(false);
    navigate("/flight-ai-packagecreate");
  };

  const handleNavigateToLandPackage = () => {
    setShowPackageTypeSelectionModal(false);
    navigate("/land-ai-packagecreate");
  };

  // Fetch data matching your routing configuration (Allows overrides to solve async state lag)
  const GetData = async (
    currentPage, 
    destinationFilter = null, 
    currentSearch = search, 
    currentStatus = status,
    currentSource = source
  ) => {
    setIsLoadingPackages(true);
    try {
      const queryParams = [];
      queryParams.push(`pageSize=${pageSize}`);
      queryParams.push(`pageNumber=${currentPage}`);

      if (currentSearch.trim()) {
        queryParams.push(`search=${encodeURIComponent(currentSearch.trim())}`);
      }
      if (currentStatus) {
        queryParams.push(`status=${encodeURIComponent(currentStatus)}`);
      }
      if (currentSource) {
        queryParams.push(`source=${encodeURIComponent(currentSource)}`);
      }
      if (destinationFilter) {
        queryParams.push(`destination=${encodeURIComponent(destinationFilter)}`);
      }

      const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}&` : "";
      const url = `${config.GETALLCOMPANYPACKAGES}${queryString}`;

      const res = await apiService.get(url, pageSize, currentPage);
      
      if (res) {
        setLists(res.data || []);
        setPageNumber(res.pageNumber || 1);
        setPageSize(res.pageSize || 20);
        setTotalPages(res.totalPages || 0);
        setTotalRecords(res.totalRecords || 0);
        setHasPreviousPage(res.previousPage !== null);
        setHasNextPage(res.nextPage !== null);
      }
    } catch (error) {
      console.error("Error fetching master packages:", error);
    } finally {
      setIsLoadingPackages(false); // Stop loader
    }
  };

  // Search Submit Handler - only calls API on button click
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    GetData(1);
  };

  // Handle destination tag click - updates search input and calls API
  const handleDestinationClick = (destination) => {
    setSearch(destination);
    GetData(1, destination, destination);
  };

  // Reset fields and force direct parameter updates
  const handleReset = () => {
    setSearch("");
    setStatus("");
    setSource("");
    GetData(1, null, "", "", ""); // Force empty strings to bypass state delay
  };

  // Handle status change from dropdown
  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

 
  // View Package Handler
  const handleViewPackage = (packageId) => {
    window.open(`/packages/view/${packageId}`, "_blank");
  };

  // Edit Package Handler
  const handleEditPackage = (packageId) => {
    navigate(`/packages/edit/${packageId}`);
  };

  // Delete Handlers
  const handleOpenDeleteModal = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        await apiService.remove(config.DELETECOMPANYPACKAGE, itemToDelete);
        await GetData(pageNumber);
      } catch (error) {
        console.error("Error removing package from list:", error);
      }
      handleCloseDeleteModal();
    }
  };

  // Pagination Helper Handlers
  const GetLastPageData = () => {
    if (hasNextPage) GetData(totalPages);
  };

  const GetFirstPageData = () => {
    if (hasPreviousPage) GetData(1);
  };

  const GetPageData = (number) => {
    setPageNumber(number);
    if (pageNumber !== number) GetData(number);
  };

  // Generate Page Number list
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => (
    <li
      className={`page-item ${pageNumber === number ? "active" : ""}`}
      key={number}
      onClick={() => GetPageData(number)}
    >
      <Link className="page-link" to="#">{number}</Link>
    </li>
  ));

  useEffect(() => {
    if (user && !loading) {
      GetData(pageNumber);
    }
  }, [user, loading]);

  if (loading) return <p className="p-4">Loading...</p>;

  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
           <PackageTabs pageName="PACKAGELIST" />
          <div className="container-fluid">
            
            {/* AI Package Type Selection Modal */}
            <Modal show={showPackageTypeSelectionModal} onHide={() => setShowPackageTypeSelectionModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>Select AI Package Type</Modal.Title>
              </Modal.Header>
              <Modal.Body className="text-center">
                <p className="mb-4">What kind of package would you like to generate?</p>
                <div className="d-grid gap-3">
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={handleNavigateToFlightPackage}
                  >
                    <i className="ri-plane-line me-2"></i>Flight Package
                    <small className="d-block text-white-50">Flights included</small>
                  </button>
                  <button className="btn btn-warning btn-lg" onClick={handleNavigateToLandPackage}>
                    <i className="ri-road-map-line me-2"></i>Land Package
                    <small className="d-block text-white-50">Flights are not included</small>
                  </button>
                </div>
              </Modal.Body>
            </Modal>

  


            <div className="col-xl-12">
              <div className="card border-0">
               
                {/* Header Section */}
                <div className="card-header border-0 pb-1 bg-transparent">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h5 className="mb-0">Packages</h5>
                    <div className="d-flex gap-2">
                      <button
                        onClick={() => navigate("/createpackage")}
                        className="btn btn-primary"
                      >
                        <i className="ri-add-line me-1"></i> Create Package
                      </button>
                      {/* <button
                        onClick={handleGenerateWithAIClick}
                        className="btn btn-success"
                        disabled={isCheckingConnects}
                      >
                        {isCheckingConnects ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="ri-robot-line me-1"></i> Create AI Package
                          </>
                        )}
                      </button> */}
                    </div>
                  </div>
                </div>

                {/* Search Inputs Row with Filters */}
                <div className="card-header border-0 pb-1 bg-transparent">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="d-flex flex-column gap-2">
                      {/* Search input row - Full width */}
                      <div className="d-flex align-items-center" style={{ gap: "12px", width: "100%" }}>
                        <div
                          className="input-group shadow-sm flex-grow-1"
                          style={{ minWidth: "400px" }}
                        >
                          <span className="input-group-text bg-white border-end-0">
                            <i className="ri-search-line text-muted"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control border-start-0"
                            placeholder="Search destinations, cover, title, travel type..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ height: "46px" }}
                          />
                        </div>

                        {/* Status Dropdown */}
                        <select
                          className="form-select"
                          value={status}
                          onChange={handleStatusChange}
                          style={{ 
                            height: "46px", 
                            width: "160px",
                            flexShrink: 0
                          }}
                        >
                          <option value="">All Status</option>
                          <option value="draft">Draft</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="published">Published</option>
                          <option value="archived">Archived</option>
                        </select>

                        <button
                          type="submit"
                          className="btn btn-primary"
                          style={{ height: "46px", whiteSpace: "nowrap" }}
                        >
                          <i className="ri-search-line me-1"></i> Search
                        </button>
                        <button
                          type="button"
                          onClick={handleReset}
                          className="btn btn-danger"
                          style={{ height: "46px", whiteSpace: "nowrap" }}
                        >
                          <i className="ri-refresh-line me-1"></i> Reset
                        </button>
                      </div>

                      {/* Destination Tags Row */}
                      <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                        <span className="text-muted fs-13 fw-semibold">Popular Destinations:</span>
                        {destinationTags.map((dest) => (
                          <button
                            key={dest}
                            type="button"
                            onClick={() => handleDestinationClick(dest)}
                            className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                            style={{ fontSize: "12px" }}
                          >
                            <i className="ri-map-pin-line me-1"></i>
                            {dest}
                          </button>
                        ))}
                      </div>

                      {/* Source Filter Row */}
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <span className="text-muted fs-13 fw-semibold">Source:</span>
                        <div className="d-flex gap-2 flex-wrap">
                          <label className="d-flex align-items-center gap-1">
                            <input
                              type="radio"
                              name="sourceFilter"
                              value="all"
                              checked={source === ""}
                              onChange={() => setSource("")}
                            />
                            <span className="fs-13">All</span>
                          </label>
                          <label className="d-flex align-items-center gap-1">
                            <input
                              type="radio"
                              name="sourceFilter"
                              value="manual"
                              checked={source === "manual"}
                              onChange={() => setSource("manual")}
                            />
                            <span className="fs-13">Manual</span>
                          </label>
                          <label className="d-flex align-items-center gap-1">
                            <input
                              type="radio"
                              name="sourceFilter"
                              value="Ai"
                              checked={source === "Ai"}
                              onChange={() => setSource("Ai")}
                            />
                            <span className="fs-13">AI</span>
                          </label>
                          <label className="d-flex align-items-center gap-1">
                            <input
                              type="radio"
                              name="sourceFilter"
                              value="dmc"
                              checked={source === "dmc"}
                              onChange={() => setSource("dmc")}
                            />
                            <span className="fs-13">DMC</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                {/* Package Grids */}
                <div className="card-body">
                  {isLoadingPackages ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading packages...</p>
                    </div>
                  ) : (
                    <>
                      <div className="row g-4">
                        {lists.length > 0 ? (
                          lists.map((v) => (
                            <div className="col-sm-12 col-md-6 col-lg-4 col-xl-3" key={v.packageId}>
                              <div className="card h-100 shadow-sm border border-light-subtle d-flex flex-column">
                                {/* Card Image and badges */}
                                <div className="position-relative">
                                  <img
                                    src={v.imageSrc || placeholderImage}
                                    alt={v.title}
                                    className="card-img-top"
                                    style={{ height: "180px", objectFit: "cover", width: "100%" }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = placeholderImage;
                                    }}
                                  />
                                  <div className="position-absolute top-0 start-0 m-2 d-flex flex-column gap-1">
                                    <span className="badge bg-primary text-uppercase">{v.travelType || "Travel"}</span>
                                   
                                   
                                  </div>
                                  <div className="position-absolute bottom-0 end-0 m-2">
                                    <span className="badge bg-success fs-12 py-1 px-2">
                                      {v.currency || "USD"} {v.salePrice ? v.salePrice.toLocaleString() : "N/A"}
                                    </span>
                                  </div>
                                </div>

                                {/* Card Content */}
                                <div className="card-body d-flex flex-column flex-grow-1 p-3">
                                  <h5 className="card-title text-dark text-truncate-2-lines mb-1" style={{ minHeight: "44px" }}>
                                    {v.title}
                                  </h5>

                                  {/* Destinations Cover */}
                                  <div className="mb-2">
                                    <span className="text-muted fs-11 text-uppercase fw-semibold d-block">Destinations Cover:</span>
                                    <span className="text-dark fs-12 fw-medium line-clamp-2" style={{ minHeight: "36px", display: "block" }}>
                                      <i className="ri-map-pin-line text-danger me-1"></i>
                                      {v.destinationsCover || "Not Specified"}
                                    </span>
                                  </div>

                                  {/* Duration and Pax */}
                                  <div className="row g-0 pt-2 border-top mt-auto fs-12 text-muted">
                                    <div className="col-6">
                                      <i className="ri-time-line me-1 text-primary"></i>
                                      {v.duration || `${v.totalDays || 0}D / ${v.totalNights || 0}N`}
                                    </div>
                                    <div className="col-6 text-end">
                                      <i className="ri-user-line me-1 text-primary"></i>
                                      {v.noOfPersons ? `${v.noOfPersons} Pax` : "N/A"}
                                    </div>
                                  </div>
                                </div>

                                {/* Action Buttons Row */}
                                <div className="card-footer bg-transparent border-top p-2">
                                  <div className="d-flex justify-content-around align-items-center">
                                    <button
                                      onClick={() => handleViewPackage(v.packageId)}
                                      className="btn btn-link text-primary p-1"
                                      title="View"
                                    >
                                      <i className="ri-eye-line fs-5"></i>
                                    </button>
                                    <button
                                      onClick={() => handleEditPackage(v.packageId)}
                                      className="btn btn-link text-warning p-1"
                                      title="Edit"
                                    >
                                      <i className="ri-pencil-line fs-5"></i>
                                    </button>
                                   
                                    <button
                                      onClick={() => handleOpenDeleteModal(v.packageId)}
                                      className="btn btn-link text-danger p-1"
                                      title="Delete"
                                    >
                                      <i className="ri-delete-bin-line fs-5"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="col-12 py-5 text-center">
                            <i className="ri-search-eye-line text-muted display-4"></i>
                            <p className="mt-2 text-muted">No Master Packages found.</p>
                          </div>
                        )}
                      </div>

                      {/* Pagination Section */}
                      <div className="align-items-center mt-4 pt-2 justify-content-between d-flex border-top">
                        <div className="flex-shrink-0">
                          <div className="text-muted">
                            Showing <span className="fw-semibold">{lists.length}</span> of{" "}
                            <span className="fw-semibold">{totalRecords}</span> Results
                          </div>
                        </div>
                        <ul className="pagination pagination-separated pagination-sm mb-0">
                          <li
                            className={`page-item ${!hasPreviousPage ? "disabled" : ""}`}
                            onClick={GetFirstPageData}
                          >
                            <Link className="page-link" to="#">Previous</Link>
                          </li>
                          {renderPageNumbers}
                          <li
                            className={`page-item ${!hasNextPage ? "disabled" : ""}`}
                            onClick={GetLastPageData}
                          >
                            <Link className="page-link" to="#">Next</Link>
                          </li>
                        </ul>
                      </div>
                    </>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog Modal */}
      <DeleteAlert
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default PackagesList;